/**
 * Container for mobile player card.
 * Maps player data to display props and handles navigation.
 * Academy cards (non-scenario) show a remove button via actionSlot.
 * @module features/squad-planning/components/MobilePlayerCard
 */

import { X } from "lucide-react-native";
import React, { memo, useCallback, useState } from "react";
import { Platform } from "react-native";
import { type Href, useRouter } from "expo-router";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { getNationalityFlagUrl } from "@/features/player-detail/utils/playerPageHeaderUtils";
import { useNavigationContext } from "@/providers/NavigationProvider";

import type {
  PlayerAction,
  SquadPlayer,
  TierName,
} from "@/features/squad-planning/types";
import { computeContractMonths } from "@/features/squad-planning/utils/computeContractMonths";
import ActionMenuContainer from "@/features/squad-planning/components/organisms/ActionMenu/ActionMenuContainer";
import RemoveAcademyDialog from "@/features/squad-planning/components/molecules/RemoveAcademyDialog";
import MobilePlayerCardView from "./MobilePlayerCardView";
import { useAppLocale } from "@/i18n";

/** Short tier labels for the badge. */
const TIER_SHORT_LABELS: Readonly<Record<TierName, string>> = {
  "First Team": "1st",
  Rotation: "Rot",
  Academy: "Acd",
  Targets: "Tgt",
};

/** Props for {@link MobilePlayerCardContainer}. */
interface MobilePlayerCardContainerProps {
  /** The squad player data to display */
  readonly player: SquadPlayer;
  /** Tier name for determining available actions in scenario mode */
  readonly tierName?: TierName;
  /** Whether a scenario is active (controls action slot rendering) */
  readonly isScenarioActive?: boolean;
  /** Optional scenario action badge for this player */
  readonly action?: PlayerAction | null;
  /**
   * Notifies the host that the card navigated to the player page, so
   * overlay hosts (e.g. the position players sheet) can close themselves
   * instead of lingering over the player page (SE-5484).
   */
  readonly onNavigate?: (playerId: string) => void;
}

/**
 * Resolves the action slot for the card header row.
 * Scenario mode: ActionMenu with sell/loan/sign/promote options.
 * Academy (non-scenario): small remove button (×).
 * @param isScenarioActive - Whether a scenario is active
 * @param tierName - Tier name or undefined
 * @param player - The player data
 * @param onRemovePress - Callback to open the remove dialog
 * @returns The action slot React node or undefined
 */
function resolveActionSlot(
  isScenarioActive: boolean,
  tierName: TierName | undefined,
  player: SquadPlayer,
  onRemovePress: () => void
): React.ReactNode {
  // SE-8409: also rendered on the Current Squad for First Team — it carries
  // only "Add Annual Salary" there (see ActionMenuContainer). Mirrors the
  // desktop table's inclusion rule.
  const isFirstTeamTier = tierName !== "Academy" && tierName !== "Targets";
  if ((isScenarioActive || isFirstTeamTier) && tierName != null) {
    // Mobile renders the bottom-sheet branch, which has no desktop trigger to
    // reveal — "ghost" is the inert choice the required prop forces us to name.
    return (
      <ActionMenuContainer
        player={player}
        tierName={tierName}
        triggerVisibility="ghost"
      />
    );
  }
  if (tierName === "Academy") {
    return (
      <Pressable
        onPress={onRemovePress}
        testID="academy-remove-btn"
        className="size-7 items-center justify-center rounded-full bg-surface-muted"
      >
        <Icon as={X} size="xs" className="text-content-muted" />
      </Pressable>
    );
  }
  return undefined;
}

/**
 * Container for the mobile player card row.
 * Resolves display values from player data and wires navigation.
 * @param props - Component props
 * @param props.player - The squad player to render
 * @param props.tierName - Tier name for action menu filtering
 * @param props.isScenarioActive - Whether a scenario is active
 * @param props.action - Optional scenario action for badge display
 * @param props.onNavigate - Notifies the host after navigating to the player
 * @returns The mobile player card view bound to player data
 */
const MobilePlayerCardContainer = ({
  player,
  tierName,
  isScenarioActive = false,
  action = null,
  onNavigate,
}: MobilePlayerCardContainerProps) => {
  const locale = useAppLocale();
  const router = useRouter();
  const { clearBreadcrumbs, pushBreadcrumb, setNavigationSource } =
    useNavigationContext();

  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);

  const handlePress = useCallback(() => {
    const playerUrl = `/players/${player.id}`;

    clearBreadcrumbs();
    pushBreadcrumb({ label: "Back to Squad", path: "/squad" });
    pushBreadcrumb({ label: player.displayName, path: playerUrl });
    setNavigationSource("squad-planning");

    router.push(playerUrl as Href);
    onNavigate?.(player.id);
  }, [
    router,
    player.id,
    player.displayName,
    clearBreadcrumbs,
    pushBreadcrumb,
    setNavigationSource,
    onNavigate,
  ]);

  const handleRemovePress = useCallback(() => setIsRemoveDialogOpen(true), []);
  const handleRemoveClose = useCallback(() => setIsRemoveDialogOpen(false), []);

  const actionSlot = resolveActionSlot(
    isScenarioActive,
    tierName,
    player,
    handleRemovePress
  );

  return (
    <>
      <MobilePlayerCardView
        locale={locale}
        displayName={player.displayName}
        teamName={player.teamName}
        primaryPosition={
          player.positionAbbreviations.length > 0
            ? player.positionAbbreviations.join(", ")
            : (player.primaryPosition ?? "\u2014")
        }
        nationalityName={player.nationalityName}
        nationalityFlagUrl={getNationalityFlagUrl(
          player.nationalityName ? { name: player.nationalityName } : null,
          Platform.OS
        )}
        tierLabel={TIER_SHORT_LABELS[player.tierName]}
        gprScore={player.gprScore}
        wage={player.wage}
        minutesPlayed={player.minutesPlayed}
        contractMonths={computeContractMonths(player.contractExpires)}
        status={player.status}
        action={action}
        actionSlot={actionSlot}
        onPress={handlePress}
      />
      <RemoveAcademyDialog
        isOpen={isRemoveDialogOpen}
        playerId={player.id}
        playerName={player.displayName}
        onClose={handleRemoveClose}
      />
    </>
  );
};

MobilePlayerCardContainer.displayName = "MobilePlayerCardContainer";

export default memo(MobilePlayerCardContainer);
